import React from 'react'
import {
  Box,
  Stack,
  Table as MuiTable,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { Alert } from '@repo/ui'
import { StatusChip, statusTone } from 'components/display/StatusChip/StatusChip'
import { palette } from 'constants/theme'
import type { SourceState } from 'services'

import { sourceNotice } from '../websiteAnalyticsModel'

/*
  Small, local table primitives — the same shape AskPanel (`/internal/usage`) uses for its
  stat tables, so the two internal analytics pages read alike. Deliberately not the shared
  `Table` (a full data grid with column management): these are short, read-only lists.
*/

export const SectionNote = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
)

export const StatTable = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <MuiTable
      size="small"
      sx={{ '& .MuiTableCell-root': { paddingTop: '10px', paddingBottom: '10px' } }}
    >
      {children}
    </MuiTable>
  </Box>
)

// `sx` is spread LAST so a caller can override the defaults above it — VRG-744's feature matrix
// pins its first two columns with `position: sticky`, which needs to beat `whiteSpace: nowrap`
// and set a background. Callers that pass nothing render exactly as before.
export const HeadCell = ({
  children,
  align,
  sx,
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
  sx?: SxProps<Theme>
}) => (
  <TableCell
    align={align ?? 'left'}
    sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', ...sx }}
  >
    {children}
  </TableCell>
)

export const Cell = ({
  children,
  align,
  strong,
  sx,
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
  strong?: boolean
  sx?: SxProps<Theme>
}) => (
  <TableCell
    align={align ?? 'left'}
    sx={{ fontSize: 13, fontWeight: strong ? 600 : 400, whiteSpace: 'nowrap', ...sx }}
  >
    {children}
  </TableCell>
)

export const EmptyRow = ({ colSpan, text }: { colSpan: number; text: string }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ fontSize: 13, color: 'text.secondary' }}>
      {text}
    </TableCell>
  </TableRow>
)

/**
 * One line per source that is not simply "ok" — fixture, not configured, failed, or an
 * informational note (e.g. "summed from daily snapshots"). A missing source is SAID,
 * never rendered as a zero.
 */
export const SourceNotices = ({
  sources,
}: {
  sources: Array<[string, SourceState | undefined]>
}) => {
  const notices = sources
    .map(([name, state]) => (state ? { state, text: sourceNotice(name, state) } : null))
    .filter((n): n is { state: SourceState; text: string } => !!n && !!n.text)
  if (notices.length === 0) return null
  return (
    <Stack spacing={2}>
      {notices.map(({ state, text }) => (
        <Notice
          key={text}
          severity={
            state.status === 'error'
              ? 'error'
              : state.status === 'ok'
                ? 'info'
                : 'warning'
          }
        >
          {text}
        </Notice>
      ))}
    </Stack>
  )
}

/**
 * An inline notice on either analytics tab: the house `Alert` from `@repo/ui`, never MUI's.
 *
 * MUI's own Alert paints `severity` from `palette.success / warning / error / info`, which
 * the VERA theme does not define, so it fell through to MUI's defaults (#2e7d32, #ed6c02…):
 * off-palette, and a different notice from the one every other platform page shows
 * (Spencer, 2026-10-02). One wrapper so neither tab reaches for MUI's again.
 */
export const Notice = ({
  severity,
  children,
}: {
  severity: 'info' | 'warning' | 'error' | 'success'
  children: React.ReactNode
}) => <Alert severity={severity} description={children} />

/*
  ── Tones ──────────────────────────────────────────────────────────────────────────────
  The semantic colours both tabs use — up/down, good/bad, caution, informational, neutral —
  read from the VERA palette and rendered through `statusTone`, the SAME function
  `StatusChip` uses for every status in the platform. So a green here is the green of an
  "Active" certificate in light AND dark, rather than MUI's `success.main` (which the VERA
  theme never defines; it was MUI's default #2e7d32 / #66bb6a).
*/
const shade = palette as unknown as Record<string, Record<number, string>>
const family = (name: string) => ({
  color: shade[name][700],
  borderColor: shade[name][200],
  backgroundColor: shade[name][100],
})

export type Tone = 'positive' | 'negative' | 'caution' | 'info' | 'neutral'

export const TONE_SWATCH: Record<Tone, { color: string; borderColor: string; backgroundColor: string }> = {
  positive: family('green'),
  negative: family('red'),
  caution: family('orange'),
  info: family('blue'),
  neutral: family('gray'),
}

/** `tone('positive').color` etc. — the mode-aware text / border / fill for a tone. */
export const useTone = () => {
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  return (tone: Tone) => statusTone(TONE_SWATCH[tone], dark)
}

/**
 * A pill on either tab ("3 on the site", "Admins only", "Tracked") — the platform's own
 * `StatusChip`, so it is the same token, height and dark treatment as every status pill.
 * `glossaryId` makes the chip explain itself on hover, exactly as status chips do.
 */
export const ToneChip = ({
  tone,
  label,
  glossaryId,
}: {
  tone: Tone
  label: string
  glossaryId?: string
}) => (
  <StatusChip
    size="small"
    status="value"
    statuses={{ value: { ...TONE_SWATCH[tone], label, glossaryId } }}
  />
)

export const dateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

export const shortDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
    : '—'
